import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faRotateLeft, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import FileDropZone from '../shared/FileDropZone';
import { extractApiError } from '../../utils/errorMessages';
import { confirmPriceImport, previewPriceImport } from '../../services/paperPricingService';
import { BATE_LABELS, formatCount, formatDay, formatPrice, requireList } from './paperPricingFormat';
import PaperPricingModal from './PaperPricingModal';

// The tiles, in the order a person checks them. Keys are the preview's `counts`.
const COUNT_TILES = [
    { key: 'new', label: 'New prices' },
    { key: 'changed', label: 'Changed prices' },
    { key: 'unchanged', label: 'Unchanged' },
    { key: 'replaced_manual', label: 'Replace a manual price' },
    { key: 'needs_a_look', label: 'Need a look' },
    { key: 'refused', label: 'Refused' },
    { key: 'new_benchmarks', label: 'New benchmark codes' },
];

const pointLabel = (bate) => BATE_LABELS[bate] || bate;

// Each list the preview can carry: its heading, what it means, and its columns.
// `key` is both the list's name and its count's name in the preview.
const SECTIONS = [
    {
        key: 'refused',
        title: 'Refused rows',
        note: 'These cells are not imported. The rest of the file still is.',
        tone: 'error',
        head: ['Sheet row', 'Column', 'Code', 'Day', 'Reason'],
        cells: (item) => [item.sheet_row, item.column, item.code, formatDay(item.date), item.reason],
    },
    {
        key: 'needs_a_look',
        title: 'Need a look',
        note: 'Check these before you confirm.',
        tone: 'warn',
        head: ['Code', 'Day', 'Price point', 'Price', 'Why'],
        cells: (item) => [item.code, formatDay(item.date), pointLabel(item.bate), formatPrice(item.value), item.reason],
        numeric: [3],
    },
    {
        key: 'new_benchmarks',
        title: 'New benchmark codes',
        note: 'Confirming adds these codes to the benchmark list. Fill in their product, location and region on Benchmarks & Instruments afterwards.',
        head: ['Code', 'Description in the file', 'Unit', 'Currency'],
        cells: (item) => [item.code, item.description, item.unit, item.currency],
    },
    {
        key: 'replaced_manual',
        title: 'Manual prices this file replaces',
        note: 'Prices typed by hand for these days are replaced by the file\'s price and marked Confirmed.',
        head: ['Code', 'Day', 'Price point', 'Typed by hand → file'],
        cells: (item) => [item.code, formatDay(item.date), pointLabel(item.bate), `${formatPrice(item.old)} → ${formatPrice(item.new)}`],
        numeric: [3],
    },
    {
        key: 'changed',
        title: 'Changed prices',
        note: 'Prices already on the board that this file changes.',
        head: ['Code', 'Day', 'Price point', 'Now → after import'],
        cells: (item) => [item.code, formatDay(item.date), pointLabel(item.bate), `${formatPrice(item.old)} → ${formatPrice(item.new)}`],
        numeric: [3],
    },
];

// Checked on arrival: a missing list or count would otherwise read as "nothing
// to import", which is the one answer this window must never invent.
const checkPreview = (data) => {
    const counts = data?.counts;
    const countsOk = Boolean(counts) && COUNT_TILES.every((tile) => typeof counts[tile.key] === 'number');
    if (!countsOk || !data.fingerprint) {
        throw new Error('The server sent an answer this screen cannot read. Refresh the page and try again.');
    }
    SECTIONS.forEach((section) => requireList(data, section.key));
    return data;
};

const PreviewSection = ({ section, items, total, truncated }) => (
    <section
        className={`pp-import-section ${section.tone ? `pp-import-section--${section.tone}` : ''}`}
        aria-label={section.title}
    >
        <h3 className="pp-import-section-title">
            {section.title} <span className="pp-import-section-count">{formatCount(total)}</span>
        </h3>
        <p className="pp-hint">{section.note}</p>
        {truncated ? (
            <p className="pp-import-truncated">
                Showing the first {formatCount(items.length)} of {formatCount(total)}.
            </p>
        ) : null}
        <div className="pp-table-scroll pp-table-scroll--short">
            <table className="pp-table pp-table--compact">
                <thead>
                    <tr>
                        {section.head.map((heading, index) => (
                            <th key={heading} scope="col" className={section.numeric?.includes(index) ? 'pp-num' : undefined}>
                                {heading}
                            </th>
                        ))}
                    </tr>
                </thead>
                {/* Preview rows carry no id of their own; their position is stable for one preview. */}
                <tbody>
                    {items.map((item, rowIndex) => (
                        <tr key={rowIndex}>
                            {section.cells(item).map((cell, index) => (
                                <td key={section.head[index]} className={section.numeric?.includes(index) ? 'pp-num' : undefined}>
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </section>
);

/**
 * Import price file (ledger row 132, AC6-AC10, AC13).
 *
 * Phil's Platts download goes in exactly as the add-in saves it. The server
 * reads it and reports what WOULD change -- new, changed (old -> new),
 * unchanged, manual prices it replaces, cells that need a look, refused cells
 * with the reason. Nothing is written until Confirm, which sends the same file
 * back with the preview's fingerprint. If the file or the board changed in
 * between, the server refuses (409) and the person previews again.
 *
 * The file can be chosen or dropped. The drop runs through the shared
 * FileDropZone, so the invitation to drop always has a handler behind it.
 */
const ImportPriceFileModal = ({ onClose, onImported }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [phase, setPhase] = useState('choose'); // choose | reading | review | saving
    const [error, setError] = useState('');
    const [stale, setStale] = useState(false);
    const inputRef = useRef(null);

    const busy = phase === 'reading' || phase === 'saving';

    const runPreview = async (chosen) => {
        setFile(chosen);
        setPreview(null);
        setError('');
        setStale(false);
        setPhase('reading');
        try {
            const data = checkPreview(await previewPriceImport(chosen));
            setPreview(data);
            setPhase('review');
        } catch (err) {
            setError(extractApiError(err, 'The file could not be read. Check it is the Platts download and try again.'));
            setPhase('choose');
        }
    };

    const confirm = async () => {
        setError('');
        setStale(false);
        setPhase('saving');
        try {
            const data = await confirmPriceImport(file, preview.fingerprint);
            onImported(data.import);
        } catch (err) {
            setError(extractApiError(err, 'The import could not be saved. Try again.'));
            setStale(err?.response?.status === 409);
            setPhase('review');
        }
    };

    const chooseAnother = () => {
        setFile(null);
        setPreview(null);
        setError('');
        setStale(false);
        setPhase('choose');
    };

    const counts = preview?.counts;
    const writesNothing = counts
        && !counts.new && !counts.changed && !counts.replaced_manual && !counts.new_benchmarks;

    const footer = (
        <>
            {preview ? (
                <button type="button" className="pp-btn pp-btn--secondary" onClick={chooseAnother} disabled={busy}>
                    Choose another file
                </button>
            ) : null}
            <span className="pp-modal-foot-spacer" />
            <button type="button" className="pp-btn pp-btn--secondary" onClick={onClose} disabled={phase === 'saving'}>
                Cancel
            </button>
            {preview ? (
                <button
                    type="button"
                    className="pp-btn pp-btn--primary"
                    onClick={confirm}
                    disabled={busy || stale}
                    title={stale ? 'Preview the file again first' : undefined}
                >
                    {phase === 'saving' ? 'Importing…' : 'Confirm import'}
                </button>
            ) : null}
        </>
    );

    return (
        <PaperPricingModal
            title="Import price file"
            eyebrow="Market Prices"
            onClose={onClose}
            busy={phase === 'saving'}
            wide
            footer={footer}
        >
            {error ? (
                <div className="pp-alert pp-alert--error" role="alert">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="pp-alert-icon" />
                    <span className="pp-alert-text">{error}</span>
                    {stale ? (
                        <button
                            type="button"
                            className="pp-btn pp-btn--secondary pp-btn--small"
                            onClick={() => runPreview(file)}
                        >
                            <FontAwesomeIcon icon={faRotateLeft} /> Preview again
                        </button>
                    ) : null}
                </div>
            ) : null}

            {!preview ? (
                <FileDropZone
                    onFiles={(files) => runPreview(files[0])}
                    onError={setError}
                    disabled={busy}
                    className="pp-drop"
                    activeClassName="pp-drop--active"
                >
                    <FontAwesomeIcon icon={faFileExcel} className="pp-drop-icon" />
                    {phase === 'reading' ? (
                        <div className="pp-drop-title">Reading {file?.name}…</div>
                    ) : (
                        <>
                            <div className="pp-drop-title">Drop the Platts price file here, or choose it</div>
                            <div className="pp-hint">
                                The Excel file (.xlsx or .xlsm) exactly as the Platts add-in saves it.
                                Nothing is saved until you confirm.
                            </div>
                            <button
                                type="button"
                                className="pp-btn pp-btn--primary"
                                onClick={() => inputRef.current?.click()}
                                disabled={busy}
                            >
                                Choose file
                            </button>
                        </>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".xlsx,.xlsm"
                        className="pp-file-input"
                        aria-label="Platts price file"
                        tabIndex={-1}
                        onChange={(event) => {
                            const chosen = event.target.files?.[0];
                            // Cleared so choosing the same file again (after fixing it) still reads it.
                            event.target.value = '';
                            if (chosen) runPreview(chosen);
                        }}
                    />
                </FileDropZone>
            ) : (
                <div className="pp-import-review">
                    <p className="pp-import-file">
                        <FontAwesomeIcon icon={faFileExcel} /> <strong>{preview.file_name}</strong>
                        {preview.sheet_name ? <> · sheet {preview.sheet_name}</> : null}
                        {preview.first_date && preview.last_date
                            ? <> · prices from {formatDay(preview.first_date)} to {formatDay(preview.last_date)}</>
                            : null}
                    </p>

                    <div className="pp-count-tiles">
                        {COUNT_TILES.map((tile) => (
                            <div key={tile.key} className={`pp-count-tile ${counts[tile.key] ? 'pp-count-tile--some' : ''}`}>
                                <div className="pp-count-value">{formatCount(counts[tile.key])}</div>
                                <div className="pp-count-label">{tile.label}</div>
                            </div>
                        ))}
                    </div>

                    {writesNothing ? (
                        <div className="pp-alert pp-alert--info">
                            Every price in this file is already on the board. Confirming records the upload and
                            changes no price.
                        </div>
                    ) : null}

                    {SECTIONS.filter((section) => counts[section.key] > 0).map((section) => (
                        <PreviewSection
                            key={section.key}
                            section={section}
                            items={preview[section.key]}
                            total={counts[section.key]}
                            // The server's flag, or the list simply holding fewer rows than
                            // its count: either way the screen says it is showing part of it.
                            truncated={Boolean(preview.truncated?.[section.key])
                                || preview[section.key].length < counts[section.key]}
                        />
                    ))}
                </div>
            )}
        </PaperPricingModal>
    );
};

export default ImportPriceFileModal;
